import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Add, Delete, Edit, TrendingUp } from '@mui/icons-material';
import { useRevenueStreams, useFinancialPeriods, usePermission } from '../hooks';
import apiClient from '../api/client';
import { colors, transitions, radius } from '../theme/theme';

export const RevenueStreamsPage = () => {
  const { can } = usePermission();
  const { periodId } = useParams<{ periodId: string }>();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [streamName, setStreamName] = useState('');
  const [streamDescription, setStreamDescription] = useState('');
  const [selectedServices, setSelectedServices] = useState<number[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  const [editingStream, setEditingStream] = useState<any>(null);

  const { streams, isLoading, error, createStream, updateStream, deleteStream } = useRevenueStreams(
    periodId ? parseInt(periodId) : undefined
  );
  const { periods } = useFinancialPeriods();

  const currentPeriod = periods?.data?.find((p: any) => p.id === parseInt(periodId || '0'));
  const isPeriodClosed = currentPeriod?.status === 'closed';

  // Fetch all county services
  useEffect(() => {
    const fetchServices = async () => {
      setServicesLoading(true);
      try {
        const response = await apiClient.get('/admin/county/revenue-streams/available-services');
        const data = response.data as any;
        setServices(Array.isArray(data) ? data : (data?.data || []));
      } catch (err) {
        console.error('Failed to fetch services:', err);
      } finally {
        setServicesLoading(false);
      }
    };

    fetchServices();
  }, []);

  const handleOpenDialog = (stream?: any) => {
    if (stream) {
      setEditingStream(stream);
      setStreamName(stream.name || '');
      setStreamDescription(stream.description || '');
      setSelectedServices(stream.services?.map((s: any) => s.id) || []);
    } else {
      setEditingStream(null);
      setStreamName('');
      setStreamDescription('');
      setSelectedServices([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStream(null);
  };

  const handleServiceChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    setSelectedServices(typeof value === 'string' ? [] : value);
  };

  const handleSaveStream = async () => {
    if (!streamName || !periodId) return;

    try {
      if (editingStream) {
        await updateStream({
          id: editingStream.id,
          data: {
            name: streamName,
            description: streamDescription,
            services: selectedServices,
          },
        });
      } else {
        await createStream({
          financial_period_id: parseInt(periodId),
          name: streamName,
          description: streamDescription,
          services: selectedServices,
        });
      }
      handleCloseDialog();
    } catch (err) {
      console.error('Failed to save stream:', err);
    }
  };

  const handleDeleteStream = async (streamId: number) => {
    if (window.confirm('Are you sure you want to delete this revenue stream?')) {
      try {
        await deleteStream(streamId);
      } catch (err) {
        console.error('Failed to delete stream:', err);
      }
    }
  };

  const handleSetTargets = (streamId: number) => {
    navigate(`/finance/streams/${streamId}/targets`);
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 85) return colors.success.main;
    if (progress >= 70) return colors.warning.main;
    return colors.error.main;
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title={`Revenue Streams - ${currentPeriod?.name || 'Period'}`}
          subtitle="Organize services into revenue streams and set targets"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Finance & Budget', path: '/finance/dashboard' },
            { label: 'Financial Periods', path: '/financial-periods' },
            { label: 'Revenue Streams' },
          ]}
          action={
            can('finance.manage_streams') ? <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()} disabled={isPeriodClosed}>
              Create Stream
            </Button> : undefined
          }
        />

        {isPeriodClosed && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This financial period is closed. Streams and targets cannot be modified.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load revenue streams. Please try again.
          </Alert>
        )}

        {((streams?.data || streams) as any[])?.length > 0 ? (
          <Grid container spacing={3}>
            {((streams?.data || streams) as any[]).map((stream: any) => {
              const progress = stream.progress || 0;
              const progressColor = getProgressColor(progress);

              return (
                <Grid key={stream.id} size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {stream.name}
                      </Typography>
                      {stream.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {stream.description}
                        </Typography>
                      )}

                      {/* Services */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Services ({stream.services?.length || 0})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {stream.services?.map((service: any) => (
                            <Chip
                              key={service.id}
                              label={service.name}
                              size="small"
                              sx={{ bgcolor: colors.primary.light, color: colors.primary.dark }}
                            />
                          ))}
                        </Box>
                      </Box>

                      {/* Targets and Progress */}
                      <Box sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Total Target
                            </Typography>
                            <Typography variant="h6" sx={{ color: colors.success.main, fontWeight: 600 }}>
                              KES {(stream.target || 0).toLocaleString()}
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="caption" color="text.secondary">
                              Collected
                            </Typography>
                            <Typography variant="h6" sx={{ color: colors.primary.main, fontWeight: 600 }}>
                              KES {(stream.collected || 0).toLocaleString()}
                            </Typography>
                          </Box>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(progress, 100)}
                          sx={{
                            height: 8,
                            borderRadius: 1,
                            bgcolor: colors.grey[200],
                            '& .MuiLinearProgress-bar': {
                              bgcolor: progressColor,
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {progress.toFixed(2)}% of target
                        </Typography>
                      </Box>

                      {/* Actions */}
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          startIcon={<TrendingUp />}
                          onClick={() => handleSetTargets(stream.id)}
                        >
                          {isPeriodClosed ? 'View Targets' : 'Set Targets'}
                        </Button>
                        {!isPeriodClosed && can('finance.manage_streams') && (
                          <>
                            <IconButton
                              size="small"
                              sx={{ color: colors.primary.main }}
                              onClick={() => handleOpenDialog(stream)}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteStream(stream.id)}
                            >
                              <Delete />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <TrendingUp sx={{ fontSize: 64, color: colors.grey[400], mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                No Revenue Streams
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Create revenue streams to organize services and set targets
              </Typography>
              {can('finance.manage_streams') && <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
                Create Stream
              </Button>}
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Stream Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{editingStream ? 'Edit Revenue Stream' : 'Create Revenue Stream'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Stream Name"
                value={streamName}
                onChange={(e) => setStreamName(e.target.value)}
                placeholder="e.g., Business Licenses"
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={streamDescription}
                onChange={(e) => setStreamDescription(e.target.value)}
                multiline
                rows={2}
              />
              <FormControl fullWidth>
                <InputLabel>Select Services</InputLabel>
                <Select
                  multiple
                  value={selectedServices}
                  onChange={handleServiceChange}
                  input={<OutlinedInput label="Select Services" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const service = services.find((s: any) => s.id === value);
                        return <Chip key={value} label={service?.name} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {services.map((service: any) => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveStream} disabled={!streamName}>
              {editingStream ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
